import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { Page, PageInner } from "@app/shared";
import { QESAlert, QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import { Link, Redirect } from "react-router-dom";
import { UploadTagsWizardState, UploadTagsWizardSteps } from "./types";
import UploadTagsWizardImportList from "./UploadTagsWizardImportList";
import UploadTagsWizardReview from "./UploadTagsWizardReview";
import React from "react";

class UploadTagsWizard extends React.Component<unknown, UploadTagsWizardState> {
  private title = "Upload Tags";
  private resourceTaggingService = new ResourceTaggingService();
  private humanReadablePageNames = {
    [UploadTagsWizardSteps.ImportList]: "Import List",
    [UploadTagsWizardSteps.Review]: "Review",
  }

  // React Built-Ins
  constructor(props: unknown) {
    super(props);
    this.state = {
      inaccessibleProjectNames: [],
      page: UploadTagsWizardSteps.ImportList,
      redirectToView: false,
      reviewData: null,
      tagsFile: null,
    };
  }

  public componentDidUpdate(prevProps: unknown, prevState: UploadTagsWizardState): void {
    if (prevState.page !== this.state.page)
      window.scrollTo(0, 0);
  }

  public render() {
    const { inaccessibleProjectNames, page, redirectToView, tagsFile } = this.state;
    if (redirectToView) {
      return <Redirect to="/tags/view" push={false} />;
    }
    return (
      <Page documentTitle={this.title}>
        {/* The design for the wizard does not have breadcrumbs */}
        <PageInner className="upload-tags-wizard">
          <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
            <h1 className="page-title mt-0 mb-0">{this.title}</h1>
          </header>
          <WizardSteps currentStep={page} steps={Object.values(this.humanReadablePageNames)} setStep={this.handleUpdatePage} />
          {inaccessibleProjectNames.length > 0 && (
            <QESAlert classNames="mt-3 mb-3" type="warning" collapseTitle="The following project names were not found on this client. Please fix or remove missing Project Names and re-upload.">
              <ul className="mb-0">
                {inaccessibleProjectNames.map((name) => <li key={`upload-tags-wizard__inaccessible-project-names__${name}`}>{name}</li>)}
              </ul>
            </QESAlert>
          )}
          <hr />
          {this.renderCurrentWizardStep()}
        </PageInner>
        <WizardNavigationBar
          leftSide={(
            <Link to="/tags/view" className="qes-button qes-button-secondary danger" data-testid="upload-tags-wizard__cancel">Cancel</Link>
          )}
          rightSide={(
            <div className="upload-tags-wizard__nav-buttons">
              {page !== UploadTagsWizardSteps.ImportList &&
                <QESButton qesType="secondary" onClick={this.handlePrev} qesTestId="upload-tags-wizard__previous">Previous</QESButton>
              }
              {page !== UploadTagsWizardSteps.Review &&
                <QESButton onClick={this.handleNext} disabled={!this.canProceed()} qesTestId="upload-tags-wizard__next" className="ml-3">Next</QESButton>
              }
              {page === UploadTagsWizardSteps.Review &&
                <QESButton onClick={this.handleSubmit} disabled={tagsFile == null} qesTestId="upload-tags-wizard__submit" className="ml-3">Submit</QESButton>
              }
            </div>
          )}
        />
      </Page>
    );
  }

  // Handlers
  private handleNext = (): void => {
    const { page } = this.state;
    switch (page) {
      case UploadTagsWizardSteps.ImportList:
        this.setState({ page: UploadTagsWizardSteps.Review });
        break;
    }
  }

  private handlePrev = (): void => {
    const { page } = this.state;
    switch (page) {
      case UploadTagsWizardSteps.Review:
        this.setState({ page: UploadTagsWizardSteps.ImportList });
        break;
    }
  }

  private handleUpdatePage = (newPage: number) => {
    const step = Object.entries(UploadTagsWizardSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as UploadTagsWizardSteps;
    this.setState({ page: UploadTagsWizardSteps[step] as unknown as UploadTagsWizardSteps });
  };

  private handleUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await this.resourceTaggingService.previewCreateAndApplyProjectTags(data);
        this.setState({ inaccessibleProjectNames: [], reviewData: response, tagsFile: data });
        resolve();
      } catch (error) {
        if (error.responseJSON != undefined) {
          reject("Some project names were not found on this client. Please fix or remove missing Project Names and re-upload.");
          this.setState({ inaccessibleProjectNames: error.responseJSON, reviewData: null, tagsFile: null });
        }
        else {
          reject(error.responseText);
          this.setState({ inaccessibleProjectNames: [], reviewData: null, tagsFile: null });
        }
      }
    });
  };

  private handleSubmit = async () => {
    const { inaccessibleProjectNames, page, tagsFile } = this.state;
    try {
      if (tagsFile == null || inaccessibleProjectNames.length !== 0 || page !== UploadTagsWizardSteps.Review)
        return false;
      await this.resourceTaggingService.createAndApplyProjectTags(tagsFile);
      this.setState({ redirectToView: true });
      return true;
    } catch (error) {
      return false;
    }
  };

  private renderCurrentWizardStep = () => {
    const { page, reviewData, tagsFile } = this.state;
    switch (page) {
      case UploadTagsWizardSteps.ImportList:
        return <UploadTagsWizardImportList handleUpload={this.handleUpload} wasSuccessful={!!tagsFile} />;
      case UploadTagsWizardSteps.Review:
        return <UploadTagsWizardReview reviewData={reviewData} />;
    }
  };

  private canProceed = (): boolean => {
    const { inaccessibleProjectNames, reviewData } = this.state;
    return inaccessibleProjectNames.length == 0 && reviewData != null;
  }

}

export default UploadTagsWizard;
